import clsx from "clsx";
import { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { DataTestId } from "types/enums/testId";

interface ButtonProps<T extends ElementType> {
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  iconCustomClasses?: string;
  compact?: boolean;
  narrow?: boolean;
  secondary?: boolean;
  dataTestId?: DataTestId | null;
  variant?: "solid" | "outline" | "ghost";
  as?: T;
}

type ButtonExtendedProps<T extends ElementType> = ButtonProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof ButtonProps<T>>;

const Button = <T extends ElementType = "button">({
  ariaLabel = "",
  children,
  className = "",
  compact = false,
  secondary = false,
  narrow = false,
  leftIcon = null,
  rightIcon = null,
  dataTestId = null,
  variant = "solid",
  iconCustomClasses = "",
  /* eslint-disable-next-line react/require-default-props */
  as,
  ...rest
}: ButtonExtendedProps<T>) => {
  const baseClasses =
    "text-lg leading-[26px] font-semibold flex justify-center gap-2 items-center cursor-pointer rounded tracking-[-0.004em] text-nowrap group";

  const styleClasses = {
    outline: "bg-fog-00 text-fog-60 ring-[1px] ring-inset ring-fog-60 hover:bg-fog-10 active:bg-fog-20",
    solid: "bg-dusk-50 text-fog-00 hover:bg-dusk-60 active:bg-dusk-70",
    ghost: "bg-transparent text-fog-80 hover:text-fog-60 active:text-fog-60",
  };

  const stylesForVariant = secondary ? styleClasses.outline : styleClasses[variant];

  const sizeClasses = clsx({
    "px-4 py-[7px] text-base": compact,
    "px-6 py-[15px]": !compact,
    "px-4": narrow,
  });

  const iconClasses = clsx({
    // directly target svg, since in Safari SVG height and width needs to be specified
    "flex h-4 w-4 items-center justify-center [&>svg]:h-4 [&>svg]:w-4": true,
    "fill-fog-60": secondary || variant === "outline",
    "fill-fog-00": !secondary || variant === "solid",
  });
  const Component = as || "button";

  return (
    <Component
      {...(ariaLabel && { "aria-label": ariaLabel })}
      {...(dataTestId && { "data-testid": dataTestId })}
      className={twMerge(baseClasses, stylesForVariant, sizeClasses, className)}
      {...rest}
    >
      {leftIcon && (
        <div className={iconClasses} data-testid="left-icon">
          {leftIcon}
        </div>
      )}

      {children && <span className="text-center">{children}</span>}

      {rightIcon && (
        <div className={twMerge(iconClasses, iconCustomClasses)} data-testid="right-icon">
          {rightIcon}
        </div>
      )}
    </Component>
  );
};

export { Button };
export type { ButtonProps };
